import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { debounce } from "lodash";
import _ from "@lodash";
import AuraTable from "app/main/apps/common/AuraTable";
import withRouter from "@fuse/core/withRouter";
import FuseUtils from "@fuse/utils";
import { authPermissions } from "app/auth";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/system";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  openDialog,
  closeDialog as fuseCloseDialog,
} from "app/store/fuse/dialogSlice";
import { showMessage } from "app/store/fuse/messageSlice";

import {
  formatDateString,
  getParams,
  mapParams,
} from "../../common/AuraFunctions";
import {
  setResetFilters,
  setSelectedShipments,
} from "../store/shipmentsSlice";
import CreateContainerForm from "./CreateContainerForm";
import getColumns from "../../e-commerce/orders/OrdersTableColumns";
import HeaderButton from "../../common/AuraComponents";
import { getOrders,selectOrders } from "../../e-commerce/store/ordersSlice";
import { createContainerShipment } from "../store/shipmentOrderSlice";


/**
 * Form Validation Schema
 */
const getSchema = () => {
  return yup.object().shape({
    order_type: yup.object().required("Required").nullable(),

    container_number: yup.string().required("Required").nullable(),

    eta_date: yup.date().required("Required").nullable(),

    location: yup.string().required("Required").nullable(),
  });
};

function ContainersContent(props) {
  const dispatch = useDispatch();
  const methods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      //  default values for create container form
      container_number: "",
      container_tracking_number: "",
      order_type: null,
      location: "",
      shipping_company: null,
      eta_date: "",
    },
    resolver: yupResolver(getSchema()),
  });

  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = methods;

  const currentUser = useSelector(({ user }) => user);
  const isManage =
    currentUser &&
    FuseUtils.hasPermission(authPermissions.manage, currentUser.role);
  const entities = useSelector(selectOrders);
  const [headers, setHeaders] = useState({});
  const [params, setParams] = useState({});
  const resetFilter = useSelector(({ eCommerceApp }) => eCommerceApp.orders.resetFilter); // changed after code
  const { orderListHeaders } = useSelector(({ eCommerceApp }) => eCommerceApp.orders);

  const listLoading = useSelector(({ eCommerceApp }) => eCommerceApp.orders.loading);
  const [pageNoUpdated, setPageNoUpdated] = useState(false);
  const [showNoDataMessage, setShowNoDataMessage] = useState(null);
  const ref = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState(""); // search for order number

  const updateParams = () => {
    const computedParams = getParams();
    setHeaders(computedParams.headerObj);
    setParams(computedParams.filterObj);
  };

  useEffect(() => {
      return () => {
        // following code will run on unmount.
        setShowNoDataMessage(null)
      };
    }, []);

  // api call for containers management list
  const debounceFn = useCallback(
    debounce((param, header) => {
      setShowNoDataMessage(null);
      // Add stage param here
      const updatedParams = { ...param, stage: "container-management" };
      dispatch(getOrders({ params: updatedParams, headers: header }))
        .unwrap()
        .then((response) => {
          const dataLength = response?.data?.length || 0;
          if (dataLength <= 0) {
            setShowNoDataMessage(true);
          }
        });
    }, 500),
    []
  );

  useEffect(() => {
    if (ref.current) {
      // Remove pageNo when searching
      let removeKey = ["stage"];
      if (pageNoUpdated === false) {
        removeKey = ["stage", "pageNo"];
      }
      const searchParams = new URLSearchParams(
        mapParams({ ...headers, ...params }, removeKey)
      );
      setPageNoUpdated(false);
      // replacestate function will not rerender the page
      if (searchParams.toString())
        window.history.replaceState(
          null,
          "",
          `${window.location.href.split("?")[0]}?${searchParams.toString()}`
        );
    } else {
      updateParams();
    }
    debounceFn(params, headers);
    ref.current = true;
    setShowNoDataMessage(null);
  }, [dispatch, debounceFn, params, headers]);

  useEffect(() => {
    if (resetFilter) {
      updateParams();
      debounceFn(params, headers);
      dispatch(setResetFilters(false));
    }
  }, [debounceFn, headers, params, resetFilter]);

  const onPageChange = (value) => {
    setPageNoUpdated(true);
    setHeaders((prev) => ({ ...prev, "App-Pagination-Num": value }));
  };

  const onPerPageChange = (value) => {
    setHeaders((prev) => ({ ...prev, "App-Pagination-Limit": value }));
  };

  const onSorting = (key, desc) => {
    const sortvalue = `${key},${desc ? "DESC" : "ASC"}`;
    if (key)
      setHeaders((prev) => ({ ...prev, "App-Pagination-Sort": sortvalue }));
    else {
      const temp = { ...headers, "App-Pagination-Sort": desc };
      // delete temp['App-Pagination-Sort']
      setHeaders(temp)
    }
  };

  const serverPaginationProps = {
    changePage: onPageChange,
    perPage: onPerPageChange,
  };

  function getTableColumns() {
    const selectedItemChange = (selected) =>
      dispatch(setSelectedShipments({ data: selected }));
    return getColumns(
      isManage,
      "container-management",
      null,
      currentUser.role,
      null,
      null,
      currentUser,
      null,
      selectedItemChange
    );
  }

  const columns = useMemo(() => getTableColumns(), []);

  const toolbarParam = {
    pageName: "container-management",
    selectionMsg: "Shipment(s) selected",
  };

  // refreshes the table api for container management
  const refreshOrders = () => {
    setParams({});
    setHeaders({});
    const newUrl = `${location.pathname}`;
    navigate(newUrl);
    debounceFn(params, headers)
  };

  // submits the container for creation
  function onSubmit(data) {
    const payload = {
      ...data,
      eta_date:
        data && data.eta_date
          ? formatDateString(data.eta_date, "yyyy-MM-dd")
          : "",
    };
    // calls the api and checks for error
    // if errors then displays a popup with warning message
    dispatch(createContainerShipment(payload))
      .then((res) => {
        if (_.has(res?.payload, "error")) {
          if (res?.payload?.message === "Container exists") {
            dispatch(
              openDialog({
                children: (
                  <>
                    <DialogTitle id="alert-dialog-title">
                      Duplicate Container Detected
                    </DialogTitle>
                    <DialogContent>
                      <DialogContentText id="alert-dialog-description">
                        A container with same Order Type and Location Exists. Do
                        you want to proceed?
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={() => {
                          dispatch(fuseCloseDialog());
                        }}
                        color="primary"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          dispatch(fuseCloseDialog());
                          proceedWithDuplicateContainer(payload);
                        }}
                        color="primary"
                        autoFocus
                      >
                        Confirm
                      </Button>
                    </DialogActions>
                  </>
                ),
              })
            );
          }
        } else {
          // Handle successful response
          // Optionally reset the form after successful submission
          dispatch(
            showMessage({
              message: "Container Created Successfully",
              type: "success",
            })
          );
          reset();
          refreshOrders();
        }
      })
  }

  // this method is used to create duplicate container if user confirms for creation
  const proceedWithDuplicateContainer = (payload) => {
    const updatedPayload = {
      ...payload,
      is_duplicate_container:true
    };

    dispatch(createContainerShipment(updatedPayload))
      .unwrap()
      .then((res) => {
        dispatch(
          showMessage({
            message: "Duplicate Container Created Successfully",
            type: "success",
          })
        );
        reset();
        refreshOrders();
      })
  };

  // to handle order number search
  const handleSearch = () => {
    if (search.trim() !== "") {
      debounceFn({ orderNo: search }, headers);
    } else {
      debounceFn(params, headers);
    }
  };

  return (
    <div className="flex-auto p-16 w-full">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box className="bg-white rounded-md shadow-md p-12 mb-24">
            <div className="flex justify-between items-center mb-16">
              <h2 className="text-lg font-semibold">Create Container</h2>
              <HeaderButton
                size="small"
                className="whitespace-nowrap mx-4"
                variant="contained"
                color="secondary"
                type="submit"
                disabled={!isValid}
              >
                Create Container
              </HeaderButton>
            </div>

            {/* Form Fields in a row */}
            <div>
              <CreateContainerForm />
            </div>
          </Box>
        </form>
      </FormProvider>
      <Box className="bg-white rounded-md p-12 shadow-md w-full">
        <div className="flex items-center gap-4 mb-4">
          <h2 className="text-lg font-semibold">Container List</h2>
          {/* Order number search in container management table */}
          <Box className="flex items-center border rounded-md overflow-hidden ml-28 bg-white shadow-sm border-2 border-[#4f46e5]">
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              variant="standard"
              placeholder="Type Order Number and Enter"
              fullWidth
              InputProps={{
                disableUnderline: true,
                className: "px-3 h-full",
                sx: {
                  height: "35px",
                  minHeight: "35px",
                  maxHeight: "35px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
              sx={{
                height: "35px",
                minHeight: "35px",
                maxHeight: "35px",
                backgroundColor: "white",
              }}
              className="bg-white h-full w-[250px]"
            />

            <IconButton
              onClick={handleSearch}
              sx={{
                height: "35px",
                width: "40px",
                borderRadius: "6px",
                backgroundColor: "secondary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "secondary.dark",
                },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Box>
        </div>

        <AuraTable
          columns={columns}
          data={entities || []}
          resetFilters
          params={params}
          toolbarParam={toolbarParam}
          isManage={props.isManage}
          headers={orderListHeaders}
          serverPaginationProps={serverPaginationProps}
          updateParams={(key, value) =>
            setParams((prev) => ({ ...prev, [key]: value }))
          }
          onSorting={onSorting}
          loading={listLoading}
          showListInfo={showNoDataMessage && showNoDataMessage === true}
          loaderProps={{
            show: !entities || entities.length === 0,
            listLoading,
            message: `There are no Containers!`,
          }}
          loaderWithSpace
          noDataMessage="There are no Containers!"
          elevation={0}
        />
      </Box>
    </div>
  );
}

export default withRouter(ContainersContent);
